import type {Node, Collection, Selection as AriaSelection, Key} from 'react-stately'

export function hasNodeWithTextValue<T>(
    node: Node<T> | null,
    col: Collection<Node<T>>,
    filter: string,
) {
    if (!node) {
        return false
    }
    if (node.textValue && isMatching(filter, node.textValue)) {
        return true
    }
    const parent = node.parentKey ? col.getItem(node.parentKey) : null
    if (parent && parent.textValue && isMatching(filter, parent.textValue)) {
        return true
    }
    const children = col.getChildren?.(node.key)
    if (children) {
        for (const child of children) {
            if (hasNodeWithTextValue(child, col, filter)) {
                return true
            }
        }
    }
    return false
}
export function isMatching(filter: string, value: string): boolean {
    if (!filter) {
        return true
    }
    return value.toLowerCase().includes(filter.toLowerCase())
}
export function selectionContainsSomeChildren<T>(
    selection: AriaSelection,
    node: Node<T>,
    col: Collection<Node<T>>,
): boolean {
    if (selection === 'all') {
        return false
    }
    const children = node.hasChildNodes && col.getChildren?.(node.key)
    if (children) {
        for (const child of children) {
            if (child.type !== 'item') {
                continue
            }
            if (selectionContainsSomeChildren(selection, child, col)) {
                return true
            }
        }
    }
    return selection.has(node.key)
}
export function selectionContainsAllChildren<T>(
    selection: AriaSelection,
    node: Node<T>,
    col: Collection<Node<T>>,
): boolean {
    if (selection === 'all') {
        return true
    }
    const children = node.hasChildNodes && col.getChildren?.(node.key)
    if (children) {
        let hasChildren = false
        for (const child of children) {
            if (child.type !== 'item') {
                continue
            }
            hasChildren = true
            if (!selectionContainsAllChildren(selection, child, col)) {
                return false
            }
        }
        if (hasChildren) {
            return true
        }
    }
    return selection.has(node.key)
}

export function getAllDescendantKeys<T>(nodeKey: Key, col: Collection<Node<T>>): Set<Key> {
    const descendants = new Set<Key>()
    const node = col.getItem(nodeKey)
    if (!node) {
        return descendants
    }

    const children = node.hasChildNodes && col.getChildren?.(node.key)
    if (children) {
        for (const child of children) {
            if (child.type === 'item') {
                descendants.add(child.key)
                const childDescendants = getAllDescendantKeys(child.key, col)
                childDescendants.forEach((key) => descendants.add(key))
            }
        }
    }

    return descendants
}

export function findSiblingParentKeys<T>(nodeKey: Key, col: Collection<Node<T>>): Key[] {
    const node = col.getItem(nodeKey)
    if (!node) {
        return []
    }

    const parentKey = node.parentKey
    if (!parentKey) {
        const rootNodes: Key[] = []
        const iterator = col.getKeys()
        for (const key of iterator) {
            const rootNode = col.getItem(key)
            if (
                rootNode &&
                rootNode.type === 'item' &&
                rootNode.key !== nodeKey &&
                rootNode.hasChildNodes
            ) {
                rootNodes.push(rootNode.key)
            }
        }
        return rootNodes
    }

    const parent = col.getItem(parentKey)
    if (!parent) {
        return []
    }

    const siblings: Key[] = []
    const children = parent.hasChildNodes && col.getChildren?.(parent.key)
    if (children) {
        for (const child of children) {
            if (child.type === 'item' && child.key !== nodeKey && child.hasChildNodes) {
                siblings.push(child.key)
            }
        }
    }

    return siblings
}

export function hasChildNodes<T>(nodeKey: Key, col: Collection<Node<T>>): boolean {
    const node = col.getItem(nodeKey)
    return node?.hasChildNodes ?? false
}

export function getParentKey<T>(nodeKey: Key, col: Collection<Node<T>>): Key | null {
    const node = col.getItem(nodeKey)
    return node?.parentKey ?? null
}

export function getSelectedParentKeys<T>(selection: Set<Key>, col: Collection<Node<T>>): Set<Key> {
    const parentKeys = new Set<Key>()
    for (const key of selection) {
        const node = col.getItem(key)
        if (node && node.hasChildNodes) {
            parentKeys.add(key)
        }
    }
    return parentKeys
}

export function getParentsWithSelectedChildren<T>(
    selection: Set<Key>,
    col: Collection<Node<T>>,
): Set<Key> {
    const parentKeys = new Set<Key>()
    for (const key of selection) {
        const node = col.getItem(key)
        if (node) {
            const parentKey = node.parentKey
            if (parentKey) {
                parentKeys.add(parentKey)
            }
        }
    }
    return parentKeys
}
